import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "~/app/queryClient";
import { getSessionUserSnapshot, isSessionResolved } from "~/domains/user/sessionRuntime";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import type { ProductDataDTO } from "~/domains/product/types/ProductDataDTO";
import { getComparisonData, type ProductComparisonData } from "./getComparisonData";
import { sendData as addProduct } from "./AddToComparison";
import { sendData as addProducts } from "./AddToComparisonBulk";
import { sendData as removeProduct } from "./RemoveFromComparison";
import { sendData as removeAllProducts } from "./RemoveAllFromComparison";
import { updateOnlyDifferencePreference } from "./updateOnlyDifferencePreference";
import type { ProductComparisonListResponseDTO } from "./dto";

export type { ProductComparisonData } from "./getComparisonData";

export type ProductComparisonMutationErrorCode =
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "LIST_FULL"
  | "INVALID_REQUEST"
  | "SERVER_ERROR"
  | "NETWORK"
  | "UNKNOWN";

export class ProductComparisonMutationError extends Error {
  readonly code: ProductComparisonMutationErrorCode;
  readonly status?: number;
  readonly productCodes?: string[];

  constructor(code: ProductComparisonMutationErrorCode, message: string, status?: number, productCodes?: string[]) {
    super(message);
    this.name = "ProductComparisonMutationError";
    this.code = code;
    this.status = status;
    this.productCodes = productCodes;
  }
}

export type ProductComparisonMutationResult =
  | { kind: "added"; productCode: string }
  | { kind: "removed"; productCode: string }
  | { kind: "bulk-added"; productCodes: string[]; products: ProductDataDTO[] }
  | { kind: "cleared" }
  | { kind: "noop" };

type ProductComparisonScope = {
  contextPath: string;
  language: string;
  siteUid: string;
  userUid: string;
};

function getComparisonScope(): ProductComparisonScope {
  return {
    contextPath: window.__SCHA_BOOT__?.contextPath ?? "",
    language: window.__SCHA_BOOT__?.language ?? "",
    siteUid: window.__SCHA_BOOT__?.siteUid ?? "",
    userUid: getSessionUserSnapshot().uid ?? "",
  };
}

export const productComparisonKeys = {
  all: ["product-comparison"] as const,
  data: () => [...productComparisonKeys.all, "data", getComparisonScope()] as const,
};

export const productComparisonQueryOptions = () =>
  queryOptions({
    queryKey: productComparisonKeys.data(),
    queryFn: getComparisonData,
    enabled: isSessionResolved() && userIsLoggedIn() && Boolean(getSessionUserSnapshot().uid),
    refetchOnWindowFocus: false,
  });

export function useProductComparisonQuery() {
  return useQuery(productComparisonQueryOptions());
}

export function useProductComparisonCount() {
  const query = useProductComparisonQuery();
  return {
    ...query,
    count: query.data?.productCodes.length ?? 0,
    productCodes: query.data?.productCodes ?? [],
  };
}

export function useProductComparisonMembership(productCode: string) {
  const query = useProductComparisonQuery();
  return {
    ...query,
    isInComparison: query.data?.productCodes.includes(productCode) ?? false,
    productCodes: query.data?.productCodes ?? [],
  };
}

function mutationError(
  operation: "add" | "bulk" | "remove" | "clear" | "preference",
  status: number,
  productCodes?: string[],
): ProductComparisonMutationError {
  let code: ProductComparisonMutationErrorCode = "UNKNOWN";
  if (status === 401) {
    code = "UNAUTHENTICATED";
  } else if (status === 403) {
    code = "FORBIDDEN";
  } else if ((operation === "add" || operation === "bulk") && status === 400) {
    code = "LIST_FULL";
  } else if (status === 400) {
    code = "INVALID_REQUEST";
  } else if (status >= 500) {
    code = "SERVER_ERROR";
  }
  return new ProductComparisonMutationError(
    code,
    `Product comparison ${operation} failed: ${status}`,
    status,
    productCodes,
  );
}

async function requireOk(response: Response, operation: "add" | "bulk" | "remove" | "clear", productCodes?: string[]) {
  if (!response.ok) {
    throw mutationError(operation, response.status, productCodes);
  }
}

function normalizeThrownError(error: unknown, operation: "preference") {
  if (error instanceof ProductComparisonMutationError) {
    return error;
  }
  const status = error && typeof error === "object" && "status" in error ? Number(error.status) : undefined;
  return new ProductComparisonMutationError(
    status ? mutationError(operation, status).code : "NETWORK",
    error instanceof Error ? error.message : `Product comparison ${operation} failed`,
    status,
  );
}

async function invalidateComparisonData() {
  await queryClient.invalidateQueries({ queryKey: productComparisonKeys.all });
}

export async function removeProductComparisonQueries() {
  await queryClient.removeQueries({ queryKey: productComparisonKeys.all });
}

export function useAddProductToComparisonMutation() {
  return useMutation<ProductComparisonMutationResult, ProductComparisonMutationError, string>({
    mutationFn: async (productCode) => {
      const response = await addProduct(productCode);
      await requireOk(response, "add", [productCode]);
      return { kind: "added", productCode };
    },
    onSuccess: invalidateComparisonData,
  });
}

export function useAddProductsToComparisonMutation() {
  return useMutation<ProductComparisonMutationResult, ProductComparisonMutationError, string[]>({
    mutationFn: async (requestedCodes) => {
      const existingCodes =
        queryClient.getQueryData<ProductComparisonData>(productComparisonKeys.data())?.productCodes ?? [];
      const productCodes = [...new Set(requestedCodes)].filter((productCode) => !existingCodes.includes(productCode));
      if (productCodes.length === 0) {
        return { kind: "noop" };
      }
      const response = await addProducts(productCodes);
      await requireOk(response, "bulk", productCodes);
      const body = response.status === 204 ? {} : ((await response.json()) as ProductComparisonListResponseDTO);
      return { kind: "bulk-added", productCodes, products: body.products ?? [] };
    },
    onSuccess: invalidateComparisonData,
  });
}

export function useRemoveProductFromComparisonMutation() {
  return useMutation<ProductComparisonMutationResult, ProductComparisonMutationError, string>({
    mutationFn: async (productCode) => {
      const response = await removeProduct(productCode);
      await requireOk(response, "remove", [productCode]);
      return { kind: "removed", productCode };
    },
    onSuccess: invalidateComparisonData,
  });
}

export function useRemoveAllProductsFromComparisonMutation() {
  return useMutation<ProductComparisonMutationResult, ProductComparisonMutationError, void>({
    mutationFn: async () => {
      const response = await removeAllProducts();
      await requireOk(response, "clear");
      return { kind: "cleared" };
    },
    onSuccess: invalidateComparisonData,
  });
}

export function useAddOrRemoveProductFromComparisonMutation() {
  const addMutation = useAddProductToComparisonMutation();
  const removeMutation = useRemoveProductFromComparisonMutation();
  const mutation = useMutation<
    ProductComparisonMutationResult,
    ProductComparisonMutationError,
    { productCode: string; isInComparison: boolean }
  >({
    mutationFn: ({ productCode, isInComparison }) =>
      isInComparison ? removeMutation.mutateAsync(productCode) : addMutation.mutateAsync(productCode),
  });

  return {
    ...mutation,
    isPending: mutation.isPending || addMutation.isPending || removeMutation.isPending,
  };
}

export function useUpdateShowDifferenceMutation() {
  return useMutation<void, ProductComparisonMutationError, boolean>({
    mutationFn: async (value) => {
      try {
        await updateOnlyDifferencePreference(value);
      } catch (error) {
        throw normalizeThrownError(error, "preference");
      }
    },
    onSuccess: async (_data, value) => {
      queryClient.setQueryData<ProductComparisonData>(productComparisonKeys.data(), (current) =>
        current ? { ...current, showDifference: value } : current,
      );
      await invalidateComparisonData();
    },
  });
}
